import React, { useRef, useState } from 'react';
import { Video, Upload, X } from 'lucide-react';
import { uploadIssueVideo, checkVideo, fmtDuration, fmtMB, MAX_ISSUE_VIDEOS } from '../utils/issueVideo';

// Short videos on the Issue Register — shared by the admin page and the portal's
// Route Issues tab. Styled only with --hp-* vars so it sits in either.

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none',
  border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-gold)',
  padding: '7px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
};

// The videos already on an issue. preload="metadata" so a list of issues doesn't
// pull every clip down; playsInline so iPhone doesn't jump to full screen.
export function IssueVideoList({ videos }) {
  if (!(videos || []).length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
      {videos.map((v, idx) => (
        <div key={v.path || idx} style={{ width: 240, maxWidth: '100%' }}>
          <video src={v.url} controls playsInline preload="metadata"
            style={{ width: '100%', maxHeight: 320, borderRadius: 6, border: '1px solid var(--hp-border)', background: '#000', display: 'block' }} />
          <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3 }}>
            Video {idx + 1}{v.duration ? ` · ${fmtDuration(v.duration)}` : ''}{v.size ? ` · ${fmtMB(v.size)}` : ''}{v.by ? ` · ${v.by}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

// Two native file inputs: one with `capture` (opens the camera in video mode — the
// side button works as the shutter), one without (camera roll / Files, for a clip
// already taken). Snapshot files[0] BEFORE clearing value — the FileList is live.
function Pickers({ onFile, disabled, label }) {
  const rec = useRef(null);
  const pick = useRef(null);
  const take = (e) => { const f = (e.target.files || [])[0]; e.target.value = ''; if (f) onFile(f); };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <input ref={rec} type="file" accept="video/*" capture="environment" style={{ display: 'none' }} onChange={take} />
      <input ref={pick} type="file" accept="video/*" style={{ display: 'none' }} onChange={take} />
      <button type="button" style={{ ...btn, opacity: disabled ? 0.5 : 1 }} disabled={disabled} onClick={() => rec.current && rec.current.click()}>
        <Video size={13} /> {label || 'Record a video'}
      </button>
      <button type="button" style={{ ...btn, opacity: disabled ? 0.5 : 1 }} disabled={disabled} onClick={() => pick.current && pick.current.click()}>
        <Upload size={13} /> Choose a video
      </button>
    </div>
  );
}

function Progress({ text, frac }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginBottom: 3 }}>{text}</div>
      <div style={{ height: 6, background: 'var(--hp-border)', borderRadius: 3, overflow: 'hidden', maxWidth: 260 }}>
        <div style={{ width: `${Math.round((frac || 0) * 100)}%`, height: '100%', background: 'var(--hp-gold)', transition: 'width .2s' }} />
      </div>
    </div>
  );
}

// On an issue that already exists: pick → upload straight away → hand back the issue.
export function AddIssueVideo({ api, issue, onAdded }) {
  const [frac, setFrac] = useState(null);
  const [error, setError] = useState('');
  const [failed, setFailed] = useState(null);   // the file, kept for Retry
  const count = (issue.videos || []).length;

  const send = async (file) => {
    setError(''); setFailed(null); setFrac(0);
    try {
      const updated = await uploadIssueVideo(api, issue.id, file, setFrac);
      setFrac(null);
      onAdded && onAdded(updated);
    } catch (e) {
      setFrac(null); setError(e.message || 'Upload failed'); setFailed(checkVideo(file) ? null : file);
    }
  };

  // Say why the picker's gone, or it looks like video upload has vanished.
  if (count >= MAX_ISSUE_VIDEOS) {
    return (
      <div style={{ marginBottom: 12, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
        {MAX_ISSUE_VIDEOS} of {MAX_ISSUE_VIDEOS} videos — that's the limit for one issue.
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Pickers onFile={send} disabled={frac !== null} label={count ? 'Record another video' : 'Record a video'} />
      {frac !== null && <Progress text={`Uploading video… ${Math.round(frac * 100)}% — keep this screen open`} frac={frac} />}
      {error && (
        <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 6 }}>
          {error}{' '}
          {failed && <button type="button" onClick={() => send(failed)} style={{ ...btn, padding: '3px 8px', marginLeft: 4 }}>Retry</button>}
        </div>
      )}
    </div>
  );
}

// On the "new issue" form there's no issue id yet, so clips are held here and
// uploaded by uploadPendingVideos() once the issue is saved.
export function PendingVideoPicker({ files, setFiles }) {
  const [error, setError] = useState('');
  const add = (f) => {
    const bad = checkVideo(f);
    setError(bad);
    if (!bad) setFiles((fs) => [...fs, f].slice(0, MAX_ISSUE_VIDEOS));
  };
  return (
    <div>
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--hp-text)' }}>
              <Video size={13} style={{ color: 'var(--hp-gold)' }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name || `Video ${i + 1}`} · {fmtMB(f.size)}</span>
              <button type="button" aria-label="Remove video" onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 2 }}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {files.length < MAX_ISSUE_VIDEOS && <Pickers onFile={add} label={files.length ? 'Record another video' : 'Record a video'} />}
      {error && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// Upload the held clips onto a just-created issue, one at a time. Returns the files
// that did NOT make it, so the form can stay open with them for a retry.
export async function uploadPendingVideos(api, issueId, files, onStatus) {
  const left = [];
  let lastErr = '';
  for (let i = 0; i < files.length; i++) {
    try {
      await uploadIssueVideo(api, issueId, files[i], (f) => onStatus && onStatus(`Uploading video ${i + 1} of ${files.length}… ${Math.round(f * 100)}%`, f));
    } catch (e) { left.push(files[i]); lastErr = e.message || 'Upload failed'; }
  }
  return { left, error: lastErr };
}

export { Progress as VideoProgress };
